import React from "react";
import { MoreHorizontal, Eye, Zap, Clock, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { money } from "@/lib/posting";

const STATE_LABEL = {
  applied: "Applied", unapplied: "Un-applied", partial: "Partial",
  force: "Force applied", gap: "Gap", processing: "Processing",
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export function ChecksList({ checks, uploads, onManage, onViewEra, onForceApply, onHistory, onReviewGap }) {
  return (
    <div className="pp-list" data-testid="pp-checks-list">
      <div className="pp-lhead">
        <span className="pp-t">Checks</span>
        <span className="pp-c">{checks.length}</span>
        <span className="pp-sortby">sorted by unapplied ↓</span>
      </div>
      <div className="pp-colgrp">
        <div />
        <div className="pp-gc">Ins. Company · EDI · Check</div>
        <div className="pp-gc">Method · Mode · Added by</div>
        <div className="pp-gc">Check Amt / Applied / Un-applied</div>
        <div className="pp-gc" style={{ textAlign: "right" }}>Action</div>
      </div>

      {uploads.map((u) => <UploadRow key={u.tempId} u={u} />)}
      {checks.map((c) => (
        <div className="pp-rowwrap" key={c.id}>
          <CheckRow c={c} onManage={onManage} onViewEra={onViewEra} onForceApply={onForceApply} onHistory={onHistory} onReviewGap={onReviewGap} />
        </div>
      ))}
    </div>
  );
}

function UploadRow({ u }) {
  const err = u.state === "error";
  return (
    <div className="pp-uprow" data-testid={`pp-upload-row-${u.tempId}`}>
      {err ? <AlertTriangle size={16} color="var(--red)" />
        : <Loader2 size={16} className="animate-spin" color={u.state === "processing" ? "var(--indigo)" : "var(--amber)"} />}
      <div className="min-w-0">
        <div className="pp-upname" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
        <div className="pp-upsub" style={err ? { color: "var(--red)" } : undefined}>
          {err ? (u.error || "Upload failed") : u.state === "processing" ? "Uploaded · processing…" : `Uploading… ${u.pct}%`}
        </div>
      </div>
      {!err && u.state === "uploading" ? (
        <div className="pp-uptrack"><div className="pp-upfill" style={{ width: `${u.pct}%` }} /></div>
      ) : null}
    </div>
  );
}

function CheckRow({ c, onManage, onViewEra, onForceApply, onHistory, onReviewGap }) {
  const ps = c.postState;
  const isGap = ps === "gap";
  const isProcessing = ps === "processing";
  const canViewEra = c.eraStatus === "era_generated" || c.status === "processed";

  const primary = isProcessing
    ? { label: "Processing", fn: null, cls: "" }
    : isGap
    ? { label: "Review Gap", fn: () => onReviewGap(c), cls: "pp-gapbtn" }
    : ps === "applied"
    ? { label: "Manage", fn: () => onManage(c), cls: "pp-manage" }
    : { label: "Post Payment", fn: () => onManage(c), cls: "pp-manage" };

  const unapTone = c.unapplied === 0 ? "pp-gre" : isGap ? "pp-red" : "pp-amb";

  return (
    <div className="pp-row pp-ck2" data-testid={`pp-check-row-${c.id}`} style={{ cursor: "pointer" }}
      onClick={() => (canViewEra ? onViewEra(c) : onManage(c))}>
      <div className={`pp-edge pp-${ps}`} />

      {/* Payer / EDI / Check — Payer + the 3 hero facts (Check No · Date · Amt) */}
      <div className="pp-payer">
        <div className="pp-pn">
          <span className="pp-pnm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.payer}</span>
          <span className={`pp-statebadge pp-${ps}`}>
            {isProcessing ? <Loader2 size={9} className="animate-spin" /> : null}{STATE_LABEL[ps] || "Un-applied"}
          </span>
        </div>
        <div className="pp-keyfacts" data-testid={`pp-keyfacts-${c.id}`}>
          <div className="pp-kf">
            <span className="pp-kf-k">Check No</span>
            <span className="pp-kf-v" data-testid={`pp-kf-checkno-${c.id}`}>{c.checkNo}</span>
          </div>
          <div className="pp-kf">
            <span className="pp-kf-k">Check Date</span>
            <span className="pp-kf-v" data-testid={`pp-kf-checkdate-${c.id}`}>{fmtDate(c.checkDate)}</span>
          </div>
        </div>
        <div className="pp-sub pp-dim">
          <span className="pp-edi">EDI {c.ediPayerId}</span>
          {c.claims != null ? <span className="pp-claims">{c.claims} claim{c.claims === 1 ? "" : "s"}</span> : null}
          <span>Dep {c.depositDate ? fmtDate(c.depositDate) : "—"}</span>
        </div>
      </div>

      {/* Meta */}
      <div className="pp-meta">
        <div className="pp-mr"><span className="pp-mk">Method</span><span className="pp-mv">{c.method}</span></div>
        <div className="pp-mr"><span className="pp-mk">Mode</span><span className="pp-mv">{c.mode}</span></div>
        <div className="pp-mr"><span className="pp-mk">Added by</span><span className="pp-mv">{c.addedBy}</span></div>
      </div>

      {/* Tally */}
      <div className="pp-tally">
        <div className="pp-tr pp-hero"><span className="pp-tk">Check Amt</span><span className="pp-tv" data-testid={`pp-tally-checkamt-${c.id}`}>{money(c.amount)}</span></div>
        <div className="pp-tr"><span className="pp-tk">Applied</span><span className={`pp-tv ${c.applied > 0 ? "pp-gre" : ""}`}>{money(c.applied)}</span></div>
        <div className="pp-tr pp-net">
          <span className="pp-tk">Un-applied</span>
          <span className={`pp-tv ${unapTone}`}>
            {money(c.unapplied)}{isGap ? <span className="pp-flag">GAP</span> : null}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="pp-actions" onClick={(e) => e.stopPropagation()}>
        <button className={`pp-pbtn ${primary.cls}`} onClick={primary.fn || undefined} disabled={!primary.fn} data-testid={`pp-primary-${c.id}`}>
          {primary.label}
        </button>
        <button className="pp-pbtn pp-history" onClick={() => onViewEra(c)} disabled={!canViewEra} data-testid={`pp-view-${c.id}`}>
          <Eye size={13} style={{ marginRight: 5 }} /> View
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="pp-ovf" data-testid={`pp-row-menu-${c.id}`} aria-label="Row actions"><MoreHorizontal size={16} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => onManage(c)} data-testid={`pp-mi-manage-${c.id}`}>
              <CheckCircle2 size={13} className="mr-2" /> Manage / Post Payment
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!canViewEra} onClick={() => onViewEra(c)} data-testid={`pp-mi-viewera-${c.id}`}>
              <Eye size={13} className="mr-2" /> View ERA / EOB
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onForceApply(c)} data-testid={`pp-mi-force-${c.id}`}>
              <Zap size={13} className="mr-2" /> Force Apply Check
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onHistory(c)} data-testid={`pp-mi-history-${c.id}`}>
              <Clock size={13} className="mr-2" /> Check Applied History
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
